import {
    ExtendedApartmentBuilding,
    ExtendedAreaProject,
    ExtendedConstructionNumber,
    ExtendedDistrict,
    ExtendedProjectPhase,
    ExtendedPropertyType,
    filterConstructionNumbers,
    filterPropertyTypes,
    findProjectPhase,
    findPropertyType,
    getDistrictStatus,
} from '../api-utilities';
import { environmentDetails } from '../environment';
import {
    ApartmentBuildingModel,
    apartmentBuildingQuery,
    areaProjectGetById,
    AreaProjectModel,
    ConstructionNumberModel,
    constructionNumberQuery,
    DistrictModel,
    districtQuery,
    ProjectPhaseModel,
    projectPhaseQuery,
    PropertyTypeModel,
    propertyTypeQuery,
} from '../generated';
import { COMPOSED_INCLUDE_STRINGS } from './included-properties';
import { getApartmentBuildingStatus } from './status/apartment-building-status';
import { constructionNumberStatus } from './status/construction-number-status';
import { getPropertyTypeStatus } from './status/property-type-status';

interface OriginalAreaModels {
    constructionNumbers: ConstructionNumberModel[];
    propertyTypes: PropertyTypeModel[];
    apartmentBuildings: ApartmentBuildingModel[];
    projectPhases: ProjectPhaseModel[];
    districts: DistrictModel[];
    areaProject: AreaProjectModel;
}

interface ExtendedAreaModels {
    constructionNumbers: ExtendedConstructionNumber[];
    propertyTypes: ExtendedPropertyType[];
    apartmentBuildings: ExtendedApartmentBuilding[];
    projectPhases: ExtendedProjectPhase[];
    districts: ExtendedDistrict[];
    areaProject: ExtendedAreaProject;
}

interface FilterFunctions {
    constructionNumber?: (constructionNumber: ConstructionNumberModel) => boolean;
    propertyType?: (propertyType: PropertyTypeModel) => boolean;
    apartmentBuilding?: (apartmentBuilding: ApartmentBuildingModel) => boolean;
    projectPhase?: (projectPhase: ProjectPhaseModel) => boolean;
    district?: (district: DistrictModel) => boolean;
}

const ExtendedAreaModelsUtility = () => {
    const areaState: {
        [areaId: string]: {
            extendedData?: ExtendedAreaModels;
            resolveArray: Function[];
            filterFunctions?: FilterFunctions;
        };
    } = {};

    async function controller(areaId: string) {
        let originalData = fetchSessionStorageData(areaId);

        if (!originalData) {
            originalData = await fetchData(areaId);
            setSessionStorageData(areaId, originalData);
        }

        // Objects without images, should not be shown on the website
        originalData = filterObjectsWithImages(originalData);

        areaState[areaId].extendedData = extendModels(originalData);

        setLoaded(areaId);
    }

    async function getData(areaId: string): Promise<ExtendedAreaModels> {
        if (!areaState[areaId]) {
            areaState[areaId] = {
                resolveArray: [],
            };

            // Controller handles fetching + extending of data
            controller(areaId);
        }

        const state = areaState[areaId];

        if (!state.extendedData) {
            /*
                if no extendedData is found, it means it's being fetched.
                Promises will be resolved after data is loaded
            */
            return new Promise((res) => {
                state.resolveArray.push(res);
            });
        } else {
            // if no extendedData is found, it comes from sessionStorage
            return state.extendedData;
        }
    }

    async function getCurrentAreaData(): Promise<ExtendedAreaModels> {
        return getData(environmentDetails.areaId!);
    }

    function setLoaded(areaId: string) {
        areaState[areaId].resolveArray.forEach((res) => {
            res(areaState[areaId].extendedData);
        });
    }

    function filterObjectsWithImages(data: OriginalAreaModels): OriginalAreaModels {
        return {
            ...data,
            constructionNumbers: data.constructionNumbers.filter(
                ({ mainImage }) => !!mainImage?.url,
            ),
            propertyTypes: data.propertyTypes.filter(({ mainImage }) => !!mainImage?.url),
            apartmentBuildings: data.apartmentBuildings.filter(({ image }) => !!image?.url),
        };
    }

    function sessionStorageId(areaId: string) {
        return `AreaObjects::${areaId}`;
    }

    function fetchSessionStorageData(areaId: string) {
        const storageData = sessionStorage.getItem(sessionStorageId(areaId));
        if (!!storageData) {
            return JSON.parse(storageData) as OriginalAreaModels;
        } else {
            return;
        }
    }

    function setSessionStorageData(areaId: string, originalData: OriginalAreaModels) {
        try {
            sessionStorage.setItem(sessionStorageId(areaId), JSON.stringify(originalData));
        } catch (error) {
            console.warn('[extended-area] -- failed to save data to storage', error);
        }
    }

    async function fetchData(areaId: string) {
        const propertyTypes = propertyTypeQuery({
            AreaProjectId: areaId,
            include: COMPOSED_INCLUDE_STRINGS.propertyType,
        });
        const constructionNumbers = constructionNumberQuery({
            AreaProjectId: areaId,
            include: COMPOSED_INCLUDE_STRINGS.constructionNumber,
        });
        const projectPhases = projectPhaseQuery({
            AreaProjectId: areaId,
            include: COMPOSED_INCLUDE_STRINGS.projectPhase,
        });
        const apartmentBuildings = apartmentBuildingQuery({
            AreaProjectId: areaId,
            include: COMPOSED_INCLUDE_STRINGS.apartmentBuilding,
        });
        const districts = districtQuery({
            AreaProjectId: areaId,
            include: COMPOSED_INCLUDE_STRINGS.district,
        });
        const areaProject = areaProjectGetById(areaId, {
            include: COMPOSED_INCLUDE_STRINGS.areaProject,
        });

        return {
            propertyTypes: await propertyTypes,
            constructionNumbers: await constructionNumbers,
            projectPhases: await projectPhases,
            apartmentBuildings: await apartmentBuildings,
            districts: await districts,
            areaProject: await areaProject,
        };
    }

    function extendModels(data: OriginalAreaModels): ExtendedAreaModels {
        const {
            constructionNumbers,
            propertyTypes,
            apartmentBuildings,
            projectPhases,
            districts,
            areaProject,
        } = data as ExtendedAreaModels;

        /*
            Extend propertTypes object
        */
        propertyTypes.forEach((propertyType) => {
            const { projectPhaseId, id } = propertyType;

            if (projectPhaseId) {
                propertyType.projectPhase = findProjectPhase(projectPhases, {
                    id: projectPhaseId,
                });
            }

            propertyType.constructionNumbers = filterConstructionNumbers(constructionNumbers, {
                propertyTypeId: id,
            });

            if (propertyType.projectPhase) {
                propertyType.status = getPropertyTypeStatus(
                    propertyType.projectPhase,
                    propertyType.constructionNumbers,
                );
            }

            propertyType.apartmentBuildings = apartmentBuildings.filter((s) =>
                s.propertyTypeIds?.includes(propertyType.id),
            );

            propertyType.objectType = 'propertyType';
        });

        /*
            Extend projectPhase object
        */
        projectPhases.forEach((projectPhase) => {
            const { id } = projectPhase;

            projectPhase.propertyTypes = filterPropertyTypes(propertyTypes, {
                projectPhaseId: id,
            });
            projectPhase.constructionNumbers = filterConstructionNumbers(constructionNumbers, {
                projectPhaseId: id,
            });

            projectPhase.objectType = 'projectPhase';
        });

        /*
            Extend apartmentBuilding object
        */
        apartmentBuildings.forEach((apartmentBuilding) => {
            const { propertyTypeIds, constructionNumberIds, projectPhaseId } = apartmentBuilding;

            if (propertyTypeIds) {
                apartmentBuilding.propertyTypes = propertyTypes.filter(({ id }) =>
                    propertyTypeIds?.includes(id),
                );
            }

            if (constructionNumberIds) {
                apartmentBuilding.constructionNumbers = constructionNumbers.filter(({ id }) =>
                    constructionNumberIds?.includes(id),
                );
            }

            if (projectPhaseId) {
                apartmentBuilding.projectPhase = findProjectPhase(projectPhases, {
                    id: projectPhaseId,
                });
            }

            if (apartmentBuilding.projectPhase) {
                apartmentBuilding.status = getApartmentBuildingStatus(
                    apartmentBuilding.projectPhase,
                    apartmentBuilding.constructionNumbers,
                );
            }

            apartmentBuilding.objectType = 'apartmentBuilding';
        });

        /*
            Extend constructionNumbers object
        */
        constructionNumbers.forEach((constructionNumber) => {
            const { propertyTypeId, projectPhaseId } = constructionNumber;

            constructionNumber.areaProject = areaProject;

            if (propertyTypeId) {
                constructionNumber.propertyType = findPropertyType(propertyTypes, {
                    id: propertyTypeId,
                });
            }

            if (projectPhaseId) {
                constructionNumber.projectPhase = findProjectPhase(projectPhases, {
                    id: projectPhaseId,
                });
            }

            constructionNumber.apartmentBuilding = apartmentBuildings.find((s) =>
                s.constructionNumberIds?.includes(constructionNumber.id),
            );

            constructionNumber.status = constructionNumberStatus(constructionNumber);

            constructionNumber.objectType = 'constructionNumber';
        });

        /*
            Extend district object
        */
        districts.forEach((district) => {
            district.status = getDistrictStatus(district, projectPhases);
            district.objectType = 'district';
        });

        areaProject.objectType = 'areaProject';

        return {
            constructionNumbers,
            propertyTypes,
            apartmentBuildings,
            projectPhases,
            districts,
            areaProject,
        };
    }

    return {
        getData,
        getCurrentAreaData,
    };
};

const extendedArea = ExtendedAreaModelsUtility();

export { extendedArea, ExtendedAreaModels };
